var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useUXPContext } from '@core/context/useUXPContext';
import useAlert from '@notifications/Alerts/useAlerts';
import useToast from '@notifications/Toast/useToast';
import { useEffect, useState } from 'react';
import { LoadingFeedback } from '@widget/loaders';
import { createRoot } from 'react-dom/client';
/**
 * @export
 * Custom hook for deleting objects with dependency checking and confirmation dialogs
 *
 * @example
 * ```tsx
 * const deleteAction = useDeleteAction();
 *
 * const handleDelete = async () => {
 *   await deleteAction.delete({
 *     model: 'Location.Location',
 *     key: locationKey,
 *     canHide: true,
 *     onSuccess: (action) => {
 *       if (action === 'delete') {
 *         navigate('/locations');
 *       } else {
 *         window.location.reload();
 *       }
 *     }
 *   });
 * };
 * ```
 */
const useDeleteAction = () => {
    const context = useUXPContext();
    const alerts = useAlert();
    const toast = useToast();
    const [loadingState, setLoadingState] = useState({
        show: false,
        message: ''
    });
    /**
     * Check if object has dependencies
     */
    const checkDependencies = (model, key) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoadingState({
                show: true,
                message: context.$L('delete-action.checking'),
                submessage: context.$L('delete-action.checking-dependencies')
            });
            // Parse model string "App.Model" format
            const [app, modelName] = model.split('.');
            const { data: result, error } = yield context.executeServiceSafe(app, `${modelName}:AuditDelete`, { checkany: 1, key }, { json: true });
            setLoadingState({ show: false, message: '' });
            if (error) {
                console.error('Failed to check dependencies:', error);
                return {
                    HasLinks: false,
                    ErrorMessage: ''
                };
            }
            const resultData = Array.isArray(result) ? (result === null || result === void 0 ? void 0 : result[0]) || null : result;
            return {
                HasLinks: (resultData === null || resultData === void 0 ? void 0 : resultData.HasLinks) > 0 || false,
                ErrorMessage: (resultData === null || resultData === void 0 ? void 0 : resultData.ErrorMessage) || ''
            };
        }
        catch (error) {
            setLoadingState({ show: false, message: '' });
            console.error('Failed to check dependencies:', error);
            return {
                HasLinks: false,
                ErrorMessage: ''
            };
        }
    });
    /**
     * Execute delete action
     */
    const executeDelete = (model, key) => __awaiter(void 0, void 0, void 0, function* () {
        setLoadingState({
            show: true,
            message: context.$L('delete-action.deleting'),
            submessage: context.$L('delete-action.please-wait')
        });
        // Parse model string "App.Model" format
        const [app, modelName] = model.split('.');
        const { error } = yield context.executeServiceSafe(app, `${modelName}:Delete`, { key });
        setLoadingState({ show: false, message: '' });
        if (error) {
            return { success: false, error };
        }
        return { success: true };
    });
    /**
     * Execute hide action
     */
    const executeHide = (model, key) => __awaiter(void 0, void 0, void 0, function* () {
        setLoadingState({
            show: true,
            message: context.$L('delete-action.hiding'),
            submessage: context.$L('delete-action.please-wait')
        });
        // Parse model string "App.Model" format
        const [app, modelName] = model.split('.');
        const { error } = yield context.executeServiceSafe(app, `${modelName}:Hide`, { key }, { json: true });
        setLoadingState({ show: false, message: '' });
        if (error) {
            return { success: false, error };
        }
        return { success: true };
    });
    /**
     * Execute make visible (unhide) action
     */
    const executeUnHide = (model, key) => __awaiter(void 0, void 0, void 0, function* () {
        setLoadingState({
            show: true,
            message: context.$L('delete-action.making-visible'),
            submessage: context.$L('delete-action.please-wait')
        });
        // Parse model string "App.Model" format
        const [app, modelName] = model.split('.');
        const { error } = yield context.executeServiceSafe(app, `${modelName}:Unhide`, { key }, { json: true });
        setLoadingState({ show: false, message: '' });
        if (error) {
            return { success: false, error };
        }
        return { success: true };
    });
    /**
     * Show delete confirmation dialog and execute action
     */
    const deleteObject = (params) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const { model, key, canHide = false, deleteTitle, deleteMessage, hideMessage, deleteSuccessMessage, hideSuccessMessage, errorTitle, errorMessage, onSuccess, onCancel, onError } = params;
        // Step 1: Check dependencies
        const auditResult = yield checkDependencies(model, key);
        // Step 2: Show appropriate confirmation dialog
        if (auditResult.HasLinks) {
            // Object has dependencies - show warning
            const warningMessage = hideMessage ||
                auditResult.ErrorMessage ||
                context.$L('delete-action.has-dependencies');
            if (canHide) {
                // Offer hide option
                const confirmed = yield alerts.confirm({
                    title: deleteTitle || context.$L('delete-action.title'),
                    content: warningMessage,
                    confirmButtonTitle: context.$L('delete-action.hide-button'),
                    cancelButtonTitle: context.$L('uxp-core.text.cancel'),
                    requireVerification: true
                });
                if (confirmed) {
                    // Execute hide
                    const result = yield executeHide(model, key);
                    if (result.success) {
                        const successMsg = hideSuccessMessage || context.$L('delete-action.hide-success');
                        toast.success(successMsg);
                        onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess('hide');
                    }
                    else {
                        const defaultErrorMessage = ((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || context.$L('delete-action.error');
                        console.error('Hide action failed:', result.error);
                        const alertTitle = typeof errorTitle === 'function'
                            ? errorTitle(result.error)
                            : errorTitle || context.$L('delete-action.error-title');
                        const alertMessage = typeof errorMessage === 'function'
                            ? errorMessage(result.error)
                            : errorMessage || defaultErrorMessage;
                        alerts.error({
                            title: alertTitle,
                            content: alertMessage
                        });
                        onError === null || onError === void 0 ? void 0 : onError(defaultErrorMessage);
                    }
                }
                else {
                    onCancel === null || onCancel === void 0 ? void 0 : onCancel();
                }
            }
            else {
                // Cannot delete or hide - just show error
                alerts.error({
                    title: deleteTitle || context.$L('delete-action.title'),
                    content: warningMessage
                });
                onCancel === null || onCancel === void 0 ? void 0 : onCancel();
            }
        }
        else {
            // No dependencies - confirm delete
            const confirmMessage = deleteMessage ||
                context.$L('delete-action.confirm-delete');
            const confirmed = yield alerts.confirm({
                title: deleteTitle || context.$L('delete-action.title'),
                content: confirmMessage,
                confirmButtonTitle: context.$L('delete-action.delete-button'),
                cancelButtonTitle: context.$L('uxp-core.text.cancel'),
                requireVerification: (_b = params === null || params === void 0 ? void 0 : params.requireVerification) !== null && _b !== void 0 ? _b : true,
                verificationText: params === null || params === void 0 ? void 0 : params.verificationText,
                verificationPrompt: params === null || params === void 0 ? void 0 : params.verificationPrompt,
                caseSensitive: params === null || params === void 0 ? void 0 : params.caseSensitive
            });
            if (confirmed) {
                // Execute delete
                const result = yield executeDelete(model, key);
                if (result.success) {
                    const successMsg = deleteSuccessMessage || context.$L('delete-action.delete-success');
                    toast.success(successMsg);
                    onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess('delete');
                }
                else {
                    const defaultErrorMessage = ((_c = result.error) === null || _c === void 0 ? void 0 : _c.message) || context.$L('delete-action.error');
                    console.error('Delete action failed:', result.error);
                    const alertTitle = typeof errorTitle === 'function'
                        ? errorTitle(result.error)
                        : errorTitle || context.$L('delete-action.error-title');
                    const alertMessage = typeof errorMessage === 'function'
                        ? errorMessage(result.error)
                        : errorMessage || defaultErrorMessage;
                    alerts.error({
                        title: alertTitle,
                        content: alertMessage
                    });
                    onError === null || onError === void 0 ? void 0 : onError(defaultErrorMessage);
                }
            }
            else {
                onCancel === null || onCancel === void 0 ? void 0 : onCancel();
            }
        }
    });
    /**
     * Make object visible (unhide) with optional confirmation
     */
    const makeVisibleObject = (params) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const { model, key, showConfirmation = true, confirmTitle, confirmMessage, successMessage, errorTitle, errorMessage, onSuccess, onCancel, onError } = params;
        // Show confirmation if requested
        if (showConfirmation) {
            const confirmed = yield alerts.confirm({
                title: confirmTitle || context.$L('delete-action.make-visible-title'),
                content: confirmMessage || context.$L('delete-action.confirm-make-visible'),
                confirmButtonTitle: context.$L('delete-action.make-visible-button'),
                cancelButtonTitle: context.$L('uxp-core.text.cancel')
            });
            if (!confirmed) {
                onCancel === null || onCancel === void 0 ? void 0 : onCancel();
                return;
            }
        }
        // Execute make visible
        const result = yield executeUnHide(model, key);
        if (result.success) {
            const successMsg = successMessage || context.$L('delete-action.make-visible-success');
            toast.success(successMsg);
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
        }
        else {
            const defaultErrorMessage = ((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || context.$L('delete-action.error');
            console.error('Make visible action failed:', result.error);
            const alertTitle = typeof errorTitle === 'function'
                ? errorTitle(result.error)
                : errorTitle || context.$L('delete-action.error-title');
            const alertMessage = typeof errorMessage === 'function'
                ? errorMessage(result.error)
                : errorMessage || defaultErrorMessage;
            alerts.error({
                title: alertTitle,
                content: alertMessage
            });
            onError === null || onError === void 0 ? void 0 : onError(defaultErrorMessage);
        }
    });
    // Render loading feedback via portal
    useEffect(() => {
        if (!loadingState.show)
            return;
        // Get or create portal root
        let portalRoot = document.getElementById('portal-root');
        if (!portalRoot) {
            portalRoot = document.createElement('div');
            portalRoot.setAttribute('id', 'portal-root');
            document.body.appendChild(portalRoot);
        }
        // Create container for this loading instance
        const loadingContainer = document.createElement('div');
        portalRoot.appendChild(loadingContainer);
        // Render loading feedback using React 18 createRoot
        const root = createRoot(loadingContainer);
        root.render(_jsx(LoadingFeedback, { show: true, message: loadingState.message, submessage: loadingState.submessage }));
        // Cleanup
        return () => {
            root.unmount();
            portalRoot === null || portalRoot === void 0 ? void 0 : portalRoot.removeChild(loadingContainer);
        };
    }, [loadingState.show, loadingState.message, loadingState.submessage]);
    return {
        delete: deleteObject,
        makeVisible: makeVisibleObject,
        checkDependencies
    };
};
export default useDeleteAction;
